/**
 * ResponsiveCardList - Bascule entre rendu cartes (phone) et rendu fallback (tablet/desktop).
 *
 * Sur < 768px : rend une liste verticale de cartes via renderCard(item).
 * Sur >= 768px : rend le fallback fourni en props (typiquement la <table> existante).
 *
 * Utilisation :
 *   <ResponsiveCardList
 *     items={interventions}
 *     renderCard={(it) => <InterventionMobileCard intervention={it} onTap={...} />}
 *     fallback={<table>...</table>}
 *   />
 *
 * Le composant ne gère PAS l'état (sélection, tri, filtres) — il déduit juste
 * la présentation du viewport. Toute logique applicative reste dans la page parent.
 */

import React from 'react';
import { useViewport } from '../../hooks/useViewport';

interface ResponsiveCardListProps<T> {
  items: T[];
  renderCard: (item: T, index: number) => React.ReactNode;
  fallback: React.ReactNode;
  emptyMessage?: string;
  /** Style optionnel sur le conteneur des cartes (mobile uniquement) */
  cardListStyle?: React.CSSProperties;
}

function ResponsiveCardList<T>({
  items,
  renderCard,
  fallback,
  emptyMessage = 'Aucun élément à afficher.',
  cardListStyle,
}: ResponsiveCardListProps<T>) {
  const { isPhone } = useViewport();

  if (!isPhone) {
    return <>{fallback}</>;
  }

  if (items.length === 0) {
    return (
      <div
        style={{
          padding: '24px 16px',
          textAlign: 'center',
          color: '#94a3b8',
          fontSize: 14,
        }}
      >
        {emptyMessage}
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        ...cardListStyle,
      }}
    >
      {items.map((item, index) => (
        <React.Fragment key={index}>{renderCard(item, index)}</React.Fragment>
      ))}
    </div>
  );
}

export default ResponsiveCardList;
